import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { getCountries } from '../../../api/countries/get-countries'
import { getPeriods } from '../../../api/periods/get-periods'
import {
  DepositAmountTypeEnum,
  depositAmountTypeEnumMapping
} from '../../../lib/types/enums/payment-condition/deposit-amount-type.enum'
import { PaymentConditionInput } from '../../../lib/types/models/payment-condition/payment-condition-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../elements/validation-buttons'
import {
  PAYMENT_CONDITION_SCHEMA,
  paymentConditionValidations,
  setBalancePaymentErrors
} from './utils'

const PaymentConditionForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<PaymentConditionInput>) => {
  const form = useForm({
    resolver: zodResolver(PAYMENT_CONDITION_SCHEMA),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  const { data: periods } = useQuery({
    queryKey: ['periods'],
    queryFn: () => getPeriods({ version: 'BASIC' })
  })

  const balancePaymentDeadline = form.watch('balancePaymentDeadline')
  const balancePaymentDaysBeforeDeparture = form.watch('balancePaymentDaysBeforeDeparture')

  useEffect(() => {
    const data = form.getValues()
    // Add validation errors
    paymentConditionValidations(data, form)
  }, [form.watch()])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={'p-4 rounded-2xl flex flex-col gap-3'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Informations sur la modalité de paiement
          </Typography>

          <div className="flex flex-col gap-2">
            <Typography variant={'tag'} color={tailwindColors.grey.DEFAULT}>
              Type
            </Typography>
            <div className="flex gap-3">
              {Object.values(DepositAmountTypeEnum).map((depositType) => (
                <FormField
                  key={depositType}
                  control={form.control}
                  name="depositType"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <ControlledRadioInput
                          {...field}
                          required
                          label={depositAmountTypeEnumMapping[depositType]}
                          checked={field.value === depositType}
                          onCheck={() => field.onChange(depositType)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>

          <FormField
            control={form.control}
            name={'periods'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    required
                    label="Période(s)"
                    variant="warning"
                    searchable
                    options={
                      periods?.data.map((period) => ({
                        label: period.name,
                        value: period.id
                      })) ?? []
                    }
                    selectedOptions={
                      field.value?.map((periodId) => ({
                        label: periods?.data.find((period) => period.id === periodId)?.name ?? '',
                        value: periodId
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'countries'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    variant={'primary'}
                    required
                    label={'Pays'}
                    options={
                      countries?.data.map((country) => ({
                        label: country.name,
                        value: country.id
                      })) ?? []
                    }
                    selectedOptions={field.value?.map((id) => ({
                      label: countries?.data.find((country) => country.id === id)?.name ?? '',
                      value: id
                    }))}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'depositPercentageOrAmountWithoutInsurance'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput {...field} type="number" label="Pourcentage ou montant fixe" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'secondPaymentDaysAfterBulletinValidation'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label="# jours après validation du bulletin pour la 2e échéance de paiement"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'secondPaymentPercentageWithoutInsurance'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="number"
                    label="Pourcentage pour le 2e paiement"
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <Typography variant={'tag'} color={tailwindColors.grey.DEFAULT}>
            Renseigner au choix une date limite ou un nombre de jours avant le départ pour
            l'échéance de paiement du solde
          </Typography>
          <FormField
            control={form.control}
            name={'balancePaymentDaysBeforeDeparture'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    disabled={!!form.watch('balancePaymentDeadline')}
                    type="number"
                    label="# jours avant le départ pour le paiement du solde"
                    onBlur={() =>
                      !balancePaymentDaysBeforeDeparture &&
                      setBalancePaymentErrors('balancePaymentDaysBeforeDeparture', form)
                    }
                    onChange={(e) => {
                      field.onChange(e)
                      form.clearErrors('balancePaymentDaysBeforeDeparture')
                      form.trigger('balancePaymentDeadline')
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="balancePaymentDeadline"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    mode="over"
                    disabled={!!form.watch('balancePaymentDaysBeforeDeparture')}
                    label="Date limite de paiement du solde"
                    onBlur={() =>
                      !balancePaymentDeadline &&
                      setBalancePaymentErrors('balancePaymentDeadline', form)
                    }
                    onChange={(date) => {
                      field.onChange(date)
                      form.clearErrors('balancePaymentDeadline')
                      form.trigger('balancePaymentDaysBeforeDeparture')
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default PaymentConditionForm
